// The publish-flow state machine (szzt-spec.md §14).
//
// §14's browser flow is ingest -> normalize -> build -> preview -> publish, where "only publish
// contacts a server, and only after OAuth" and anonymous use is "preview-only" (§4). This module is
// the pure, deterministic model of that flow: a reducer over explicit events, with the load-bearing
// invariants encoded as transition GUARDS rather than left to UI wiring. Two guards gate a publish:
// it can begin only from the `previewed` state — reached only after a successful build (`ready`) AND
// the sandboxed preview has actually rendered (§14 makes preview a MUST step, not optional) — AND
// only when the caller is authenticated. So `build -> preview -> publish` is a real ordering in the
// state graph: publish is UNREACHABLE directly from `ready`, exactly as §14 requires. The async work
// (building, previewing, publishing) is performed by the caller; its outcomes are fed back as events,
// so the machine itself has no clock, network, or side effects and is fully unit-testable. This is a
// first-class FSM by design: "can't publish yet", "must preview first", and "publish needs auth" are
// single transitions, not scattered timing checks or a bypassing `hasPreviewed` boolean.

import type { BuildResult } from "@szzt/core";
import type { PublishResult } from "@szzt/publish";

/** Which phase an error occurred in, so the UI can route the message and offer the right retry. */
export type FlowPhase = "build" | "publish";

/**
 * The flow state. `ready` and beyond carry the `built` result so preview stays available throughout.
 *
 * `ready` vs `previewed` is the §14 preview gate: `ready` means the build succeeded and the preview
 * can be mounted, but the sandboxed preview has NOT yet rendered, so publish stays disabled;
 * `previewed` means the sandboxed iframe has rendered (the publisher has seen their site), which is
 * the only state a publish may begin from.
 */
export type FlowState =
  | { step: "idle" }
  | { step: "building" }
  | { step: "ready"; built: BuildResult }
  | { step: "previewed"; built: BuildResult }
  | { step: "publishing"; built: BuildResult }
  | { step: "published"; built: BuildResult; result: PublishResult }
  | { step: "error"; phase: FlowPhase; message: string; built?: BuildResult };

/** Events that drive the flow. Async outcomes (build/preview/publish success or failure) fed back here. */
export type FlowEvent =
  | { type: "BUILD_START" }
  | { type: "BUILD_OK"; built: BuildResult }
  | { type: "BUILD_ERR"; message: string }
  /** The sandboxed preview iframe has rendered the built site (§14 preview step); unlocks publish. */
  | { type: "PREVIEW_OK" }
  /** `authed` MUST reflect a completed OAuth session; publishing anonymously is refused (§14, §4). */
  | { type: "PUBLISH_START"; authed: boolean }
  | { type: "PUBLISH_OK"; result: PublishResult }
  | { type: "PUBLISH_ERR"; message: string }
  | { type: "RESET" };

/** The initial state. */
export const initialFlow: FlowState = { step: "idle" };

/**
 * Whether a publish may begin: only from `previewed` (a build exists AND its sandboxed preview has
 * rendered, so the publisher has seen their site) and only when authenticated (§14, §4). This is the
 * same guard the reducer applies to `PUBLISH_START`, exposed so a UI can enable/disable the publish
 * control without duplicating the rule. A `ready` build that has not yet previewed is NOT publishable.
 */
export function canPublish(state: FlowState, authed: boolean): boolean {
  return state.step === "previewed" && authed;
}

/**
 * Pure flow transition. Illegal transitions (e.g. publishing before a build, publishing while
 * unauthenticated, or a `BUILD_OK` with no build in flight) return the state UNCHANGED, so the
 * machine never enters an impossible state and callers can treat any event as safe to dispatch.
 */
export function reduce(state: FlowState, event: FlowEvent): FlowState {
  switch (event.type) {
    case "RESET":
      return initialFlow;

    case "BUILD_START":
      // A new build can start from any settled state, but not mid-build/mid-publish (serialize).
      if (state.step === "building" || state.step === "publishing") return state;
      return { step: "building" };

    case "BUILD_OK":
      if (state.step !== "building") return state;
      return { step: "ready", built: event.built };

    case "BUILD_ERR":
      if (state.step !== "building") return state;
      return { step: "error", phase: "build", message: event.message };

    case "PREVIEW_OK":
      // The §14 preview gate: a rendered sandboxed preview promotes `ready` -> `previewed`, the only
      // state a publish may begin from. Fired from anywhere else (e.g. a stray iframe load event) it
      // is a no-op, so the guard can't be tripped without a real build having previewed.
      if (state.step !== "ready") return state;
      return { step: "previewed", built: state.built };

    case "PUBLISH_START":
      // Load-bearing guard (§14, §4): only from `previewed` (built AND previewed), only when authed.
      if (!canPublish(state, event.authed)) return state;
      return { step: "publishing", built: (state as { built: BuildResult }).built };

    case "PUBLISH_OK":
      if (state.step !== "publishing") return state;
      return { step: "published", built: state.built, result: event.result };

    case "PUBLISH_ERR":
      if (state.step !== "publishing") return state;
      // Carry `built` so the UI can keep previewing and offer a retry.
      return { step: "error", phase: "publish", message: event.message, built: state.built };
  }
}
